import React, {
  useContext, useCallback, useEffect, useRef,
} from 'react';

import { SettingsIcon } from '@vlabs/icons';
import {
  Page, PageContent, PageHeader, usePageContext,
} from '@vlabs/monokit/shared/ui/page';
import { Tabs, Tab } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import { useTranslation } from 'react-i18next';

import { AddButtonWithDropdown } from './add-button-with-dropdown/AddButtonWithDropdown';
import { QueueContext, QueueContextProvider } from './queue-context/QueueContext';
import { QueueEventsContextProvider } from './queue-context/QueueEventsContext';
import st from './QueuePage.module.sass';
import { MonitoringTab, StatisticTab } from './tabs';

const QUEUE_TABS = ['monitoring', 'statistic'];

function QueuePage() {
  const { getTitleComponent } = usePageContext();
  const tabsWrapperRef = useRef(null);
  const {
    setIsSettingsModalOpen,
    activeTab,
    setActiveTab,
    setStreamIdsForStatistic,
  } = useContext(QueueContext);

  const { t } = useTranslation();

  const actions = (
    <div className={st.Actions}>
      <AddButtonWithDropdown />
      <IconButton
        data-testid="OpenPageSettingsButton"
        icon={<SettingsIcon />}
        kind="secondary"
        onClick={() => setIsSettingsModalOpen(true)}
        shape="square"
        variant="outlined"
      />
    </div>
  );

  const handleTabActivation = useCallback((tab) => {
    if (tab === activeTab) return;
    // при переключении таба сбрасываем выбранные для статистики очереди
    setStreamIdsForStatistic((prev) => (prev.length ? [] : prev));
    setActiveTab(tab);
  }, [activeTab, setActiveTab, setStreamIdsForStatistic]);

  // Tabs из uikit вызывает onTabActivation при обновлениях
  // поэтому слушаем только действия пользователя
  const handleTabHeaderInteraction = useCallback((target) => {
    const header = target?.closest('.Tabs__Header');

    if (!header) return;

    const tab = QUEUE_TABS[Number(header.dataset.index)];

    if (!tab) return;

    handleTabActivation(tab);
  }, [handleTabActivation]);

  useEffect(() => {
    const node = tabsWrapperRef.current;

    if (!node) return undefined;

    const handleClick = (event) => {
      handleTabHeaderInteraction(event.target);
    };

    node.addEventListener('click', handleClick);
    node.addEventListener('keyup', handleClick);

    return () => {
      node.removeEventListener('click', handleClick);
      node.removeEventListener('keyup', handleClick);
    };
  }, [handleTabHeaderInteraction]);

  const tabs = (
    <div ref={tabsWrapperRef}>
      <Tabs activeTabIndex={QUEUE_TABS.findIndex((tab) => tab === activeTab)}>
        <Tab
          header={t('queue:tabs.monitoring tab.header')}
          name="monitoring"
        />
        <Tab
          header={t('queue:tabs.statistic tab.header')}
          name="statistic"
        />
      </Tabs>
    </div>
  );

  return (
    <Page className={activeTab === 'statistic' ? st.QueuePage : ''}>
      <PageContent>
        <PageHeader
          actions={activeTab === 'monitoring' && actions}
          title={(
            <React.Fragment>
              {getTitleComponent(t('queue:queue page.title'))}
              {tabs}
            </React.Fragment>
          )}
        />
        {activeTab === 'monitoring' && <QueueEventsContextProvider><MonitoringTab /></QueueEventsContextProvider>}
        {activeTab === 'statistic' && <StatisticTab />}
      </PageContent>
    </Page>
  );
}

function QueuePageWithContext() {
  return <QueueContextProvider><QueuePage /></QueueContextProvider>;
}

export { QueuePageWithContext as QueuePage };
